import React, { useState, useEffect } from 'react';
import { useSeason } from '../../context/SeasonContext';
import { usePlayer } from '../../context/PlayerContext';
import { Calendar, Plus, Edit, Trash2, Check, X, ChevronDown, HelpCircle, BarChart } from 'lucide-react';

const SeasonSelector = () => {
  const { 
    seasons,
    activeSeason,
    isLoading,
    error,
    addSeason,
    updateSeasonData,
    removeSeason,
    changeActiveSeason,
    migrateGames
  } = useSeason();
  
  const { selectedPlayerId, toggleViewAllSeasons, viewingAllSeasons } = usePlayer();
  
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editSeasonId, setEditSeasonId] = useState(null);
  const [newSeasonData, setNewSeasonData] = useState({
    name: '',
    year: new Date().getFullYear(),
    description: '',
    totalGames: 162
  });
  const [migrationStatus, setMigrationStatus] = useState(null);
  
  // Reset form when toggling add form visibility
  useEffect(() => {
    if (!showAddForm) {
      setNewSeasonData({
        name: '',
        year: new Date().getFullYear(),
        description: '',
        totalGames: 162
      });
    }
  }, [showAddForm]);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSeasonData({
      ...newSeasonData,
      [name]: name === 'year' ? parseInt(value, 10) : value
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newSeasonData.name) {
      alert('Please enter a season name');
      return;
    }
    
    try {
      if (editSeasonId) {
        // Update existing season
        await updateSeasonData(editSeasonId, newSeasonData);
        setEditSeasonId(null);
      } else {
        // Create new season
        const newSeasonId = await addSeason(newSeasonData);
        
        // If this is the first season and we have games without seasons,
        // offer to migrate them
        if (seasons.length === 0 && selectedPlayerId) {
          const shouldMigrate = window.confirm('Would you like to associate your existing games with this new season?');
          if (shouldMigrate) {
            setMigrationStatus('migrating');
            const count = await migrateGames(selectedPlayerId, newSeasonId);
            setMigrationStatus(`Successfully migrated ${count} games to the new season`);
            setTimeout(() => setMigrationStatus(null), 3000);
          }
        }
      }
      
      // Close form
      setShowAddForm(false);
    } catch (err) {
      console.error('Error saving season:', err);
      alert('Failed to save season. Please try again.');
    }
  };
  
  // Handle season edit
  const handleEdit = (season) => {
    setNewSeasonData({
      name: season.name,
      year: season.year,
      description: season.description || '',
      totalGames: season.totalGames || 162
    });
    setEditSeasonId(season.id);
    setShowAddForm(true);
    setIsDropdownOpen(false);
  };
  
  // Handle season delete
  const handleDelete = async (seasonId) => {
    if (window.confirm('Are you sure you want to delete this season? This will not delete the games, but they will no longer be associated with a season.')) {
      try {
        await removeSeason(seasonId);
      } catch (err) {
        console.error('Error deleting season:', err);
        alert('Failed to delete season. Please try again.');
      }
    }
  };
  
  // Handle season selection
  const handleSelectSeason = async (seasonId) => {
    try {
      // If we're currently viewing all seasons, turn that off
      if (viewingAllSeasons) {
        toggleViewAllSeasons(false);
      }
      
      // Change the active season
      await changeActiveSeason(seasonId);
      setIsDropdownOpen(false);
    } catch (err) {
      console.error('Error selecting season:', err);
      alert('Failed to change season. Please try again.');
    }
  };
  
  // Cancel form
  const handleCancel = () => {
    setShowAddForm(false);
    setEditSeasonId(null);
  };
  
  return (
    <div className="relative">
      {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
      
      {!showAddForm ? (
        <>
          {/* Season Selector Dropdown */}
          <div className="flex items-center">
            <div 
              className="flex items-center space-x-2 p-2 border border-gray-300 dark:border-dark-400 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-300"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <Calendar size={16} className="text-blue-600 dark:text-blue-400" />
              <span className="text-gray-700 dark:text-darkText-primary">
                {activeSeason ? activeSeason.name : 'All Seasons'}
              </span>
              <ChevronDown size={16} className="text-gray-500 dark:text-darkText-secondary" />
            </div>
            
            <button
              type="button"
              className="ml-2 p-2 bg-blue-600 dark:bg-blue-700 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center justify-center"
              onClick={() => setShowAddForm(true)}
              title="Add New Season"
            >
              <Plus size={16} className="flex-shrink-0" />
            </button>
            
            <a
              href="/LeaguesAndSeasonsGuide.html"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 p-2 bg-gray-100 dark:bg-dark-300 text-gray-600 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-dark-400 flex items-center justify-center"
              title="Help: Understanding Leagues and Seasons"
            >
              <HelpCircle size={16} className="flex-shrink-0" />
            </a>
          </div>
          
          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute z-10 mt-1 w-64 bg-white dark:bg-dark-200 border border-gray-200 dark:border-dark-400 rounded-md shadow-lg">
              <ul className="py-1">
                {/* All Seasons Option */}
                <li 
                  className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-dark-300 flex justify-between items-center border-b border-gray-200 dark:border-dark-400"
                >
                  <div 
                    className={`flex items-center cursor-pointer ${
                      viewingAllSeasons ? 'font-semibold text-purple-600 dark:text-purple-400' : 'text-gray-700 dark:text-darkText-primary'
                    }`}
                    onClick={() => {
                      toggleViewAllSeasons(true);
                      setIsDropdownOpen(false);
                    }}
                  >
                    <BarChart size={16} className="mr-2 text-purple-500" />
                    All Seasons (Career Stats)
                    {viewingAllSeasons && <Check size={16} className="ml-2 text-green-500" />}
                  </div>
                </li>
                
                {isLoading ? (
                  <li className="px-4 py-2 text-gray-500 dark:text-darkText-secondary">Loading seasons...</li>
                ) : seasons.length === 0 ? (
                  <li className="px-4 py-2 text-gray-500 dark:text-darkText-secondary">No seasons found</li>
                ) : (
                  seasons.map(season => (
                    <li
                      key={season.id}
                      className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-dark-300 flex justify-between items-center"
                    >
                      <div
                        className={`flex items-center cursor-pointer ${
                          season.isActive && !viewingAllSeasons ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-darkText-primary'
                        }`}
                        onClick={() => handleSelectSeason(season.id)}
                      >
                        {season.name} ({season.year})
                        {season.isActive && !viewingAllSeasons && <Check size={16} className="ml-2 text-green-500" />}
                      </div>
                      
                      <div className="flex space-x-1">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(season);
                          }}
                          className="text-gray-500 dark:text-darkText-secondary hover:text-gray-700 dark:hover:text-darkText-primary"
                          title="Edit Season"
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(season.id);
                          }}
                          className="text-gray-500 dark:text-darkText-secondary hover:text-red-500"
                          title="Delete Season"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </>
      ) : (
        /* Season Form */
        <div className="p-4 border border-gray-200 dark:border-dark-400 rounded-md bg-white dark:bg-dark-200">
          <h3 className="text-lg font-medium mb-4 dark:text-darkText-primary">
            {editSeasonId ? 'Edit Season' : 'Add New Season'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-secondary mb-1">
                Season Name
              </label>
              <input
                type="text"
                name="name"
                value={newSeasonData.name}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 dark:border-dark-400 rounded-md dark:bg-dark-300 dark:text-darkText-primary"
                placeholder="e.g. Spring Season"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-secondary mb-1">
                Year
              </label>
              <input
                type="number"
                name="year"
                value={newSeasonData.year}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 dark:border-dark-400 rounded-md dark:bg-dark-300 dark:text-darkText-primary"
                min="1900"
                max="2100"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-secondary mb-1">
                Total Games in Season
              </label>
              <input
                type="number"
                name="totalGames"
                value={newSeasonData.totalGames}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 dark:border-dark-400 rounded-md dark:bg-dark-300 dark:text-darkText-primary"
                min="1"
                max="500"
                required
              />
              <p className="text-xs text-gray-500 dark:text-darkText-tertiary mt-1">
                This will be used to calculate "On Pace" statistics
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-secondary mb-1">
                Description (Optional)
              </label>
              <textarea
                name="description"
                value={newSeasonData.description}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 dark:border-dark-400 rounded-md dark:bg-dark-300 dark:text-darkText-primary"
                placeholder="Enter season description"
                rows="3"
              />
            </div>
            
            {migrationStatus && (
              <div className="p-2 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-md">
                {migrationStatus}
              </div>
            )}
            
            <div className="flex space-x-2 pt-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center"
              >
                <Check size={16} className="mr-1" />
                {editSeasonId ? 'Update Season' : 'Add Season'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 dark:bg-dark-400 text-gray-700 dark:text-darkText-primary rounded-md hover:bg-gray-300 dark:hover:bg-dark-300 flex items-center"
              >
                <X size={16} className="mr-1" />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SeasonSelector;
